/**
 * DimensionWeightSliders.jsx — Employer v1.1 Phase 0.6 Slice B.
 *
 * 5 sliders + live Total=N indicator + Restore defaults link.
 * Defaults mirror core.emp_role_criteria.DEFAULT_DIMENSION_WEIGHTS.
 *
 * Backend rule: when all 5 dimensions are present, sum MUST equal
 * 100. We surface the rule live so the operator can fix it before
 * the PATCH /criteria fires.
 */
import { useMemo } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';

export const DIMENSIONS = [
  'seniority',
  'domain_alignment',
  'evidence_strength',
  'location',
  'risk',
];

export const DEFAULT_DIMENSION_WEIGHTS = {
  seniority:         25,
  domain_alignment:  30,
  evidence_strength: 25,
  location:          10,
  risk:              10,
};

export default function DimensionWeightSliders({ value, onChange }) {
  const weights = useMemo(() => {
    const out = { ...DEFAULT_DIMENSION_WEIGHTS };
    if (value && typeof value === 'object') {
      for (const k of DIMENSIONS) {
        if (typeof value[k] === 'number') out[k] = value[k];
      }
    }
    return out;
  }, [value]);

  const total = DIMENSIONS.reduce((s, k) => s + (weights[k] || 0), 0);
  const sumOk = total === 100;

  function setOne(k, n) {
    const v = Math.max(0, Math.min(100, Math.round(n)));
    onChange({ ...weights, [k]: v });
  }

  function restoreDefaults() {
    onChange({ ...DEFAULT_DIMENSION_WEIGHTS });
  }

  return (
    <div className="space-y-4" data-testid="dimension-weight-sliders">
      <div className="grid grid-cols-1 gap-4">
        {DIMENSIONS.map((k) => (
          <div key={k} className="space-y-1" data-testid={`weight-row-${k}`}>
            <div className="flex items-center justify-between">
              <Label className="text-sm capitalize text-slate-200">
                {k.replace(/_/g, ' ')}
              </Label>
              <span
                className="font-mono text-sm text-slate-100"
                data-testid={`weight-value-${k}`}
              >
                {weights[k]}
              </span>
            </div>
            <Slider
              min={0}
              max={100}
              step={1}
              value={[weights[k]]}
              onValueChange={(arr) => setOne(k, arr[0])}
              data-testid={`weight-slider-${k}`}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <span
          className={`text-sm font-medium ${sumOk ? 'text-emerald-200' : 'text-amber-200'}`}
          data-testid="weight-total"
        >
          Total = {total}{sumOk ? ' ✓' : ' · must equal 100'}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={restoreDefaults}
          data-testid="weight-restore-defaults"
        >
          <RotateCcw className="mr-2 h-3 w-3" />
          Restore defaults
        </Button>
      </div>
    </div>
  );
}
